import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { getIn } from 'formik';
import PropTypes from 'prop-types';
import momentTz from 'moment-timezone';
import Box from '@material-ui/core/Box';
import { getTimezoneDate } from 'utils';
import DatePicker from 'react-datepicker';
import useStyles from './DateTimePicker.styled';
import TextFieldStyled from './TextField.styled';
import 'react-datepicker/dist/react-datepicker.css';
import { ErrorMessage } from 'components/FormFields';

const TIME_IN_SECONDS = 24 * 60 * 60;

const DateTimePickerField = ({
  id,
  utc,
  day,
  time,
  label,
  hours,
  format,
  margin,
  maxDate,
  minDate,
  timezone,
  required,
  editable,
  disabled,
  className,
  InputProps,
  dateFormat,
  timeFormat,
  isClearable,
  isStateless = false,
  asyncErrors,
  handleChange,
  timeIntervals,
  showTimeSelect,
  popperModifiers,
  popperPlacement,
  showTimeSelectOnly,
  field: { name, value },
  form: { errors, touched, setFieldValue },
  ...props
}) => {
  const classes = useStyles();
  const [datetime, setDatetime] = useState(value ? getTimezoneDate(value, timezone) : null);

  const handleOnChange = (_date) => {
    setDatetime(_date);
    let date = timezone
      ? momentTz(_date).tz(timezone, true).format()
      : moment(_date).format(format);
    if (utc) {
      date = timezone ? moment(date).utc().format() : moment(date).toISOString(true);
    }
    if (time) {
      // Get hour
      const hours = moment(_date).get('hour') * 60 * 60;
      const minutes = moment(_date).get('minutes') * 60;
      const time = (hours + minutes) / TIME_IN_SECONDS;
      // Get the integer part absolute to evaluate as "day"
      const dayValue = Math.trunc(Math.abs(day));
      // Append the fraction of n/24
      date = dayValue + time;
    }
    setFieldValue(name, _date ? date : null);
    if (handleChange) {
      handleChange(name, _date);
    }
  };

  const handleShowError = () => {
    if (asyncErrors) {
      return asyncErrors;
    } else if (errors && getIn(errors, name) && getIn(touched, name)) {
      return getIn(errors, name);
    }
  };

  useEffect(() => {
    if (day && value && !isStateless) {
      const time = Math.round((value - Math.round(value)) * TIME_IN_SECONDS);
      const minutes = time / 60;
      const hours = Math.trunc(minutes / 60);
      const date = getTimezoneDate(
        moment().set({
          hour: hours,
          minutes: minutes - hours * 60,
        }),
        timezone
      );
      if (time) {
        handleOnChange(date);
      }
      setDatetime(date);
    }
  }, [day, value]); // eslint-disable-line

  useEffect(() => {
    if (value && !day && !hours && !isStateless) {
      const date = getTimezoneDate(value, timezone);
      if (!isNaN(date.getTime())) {
        setDatetime(value ? date : null);
      }
    }
  }, [value, timezone, day, hours]);

  return (
    <Box className={`${classes.datepickerContainer} ${className || ''}`}>
      <DatePicker
        name={name}
        minDate={minDate}
        maxDate={maxDate}
        timeCaption="Time"
        selected={datetime}
        disabled={disabled}
        id={id || 'datePicker'}
        timeIntervals={timeIntervals}
        isClearable={isClearable}
        timeFormat={timeFormat}
        required={required}
        // formats allowed to read, only first one will be used to show date
        dateFormat={[dateFormat, 'MMddyyyy']}
        customInput={
          <TextFieldStyled
            label={label}
            margin="none"
            InputProps={InputProps || {}}
            {...props}
            editable={(editable || false).toString()}
          />
        }
        className={margin === 'none' ? classes.datepickerStyle : classes.datepicker}
        showTimeSelect={showTimeSelect}
        showTimeSelectOnly={showTimeSelectOnly}
        popperPlacement={popperPlacement}
        popperModifiers={popperModifiers}
        onChange={(date) => handleOnChange(date)}
      />
      {handleShowError() && (
        <ErrorMessage className={classes.errorMsg}>{handleShowError()}</ErrorMessage>
      )}
    </Box>
  );
};

DateTimePickerField.propTypes = {
  utc: PropTypes.bool,
  maxDate: PropTypes.any,
  minDate: PropTypes.any,
  format: PropTypes.string,
  disabled: PropTypes.bool,
  timeFormat: PropTypes.string,
  dateFormat: PropTypes.string,
  asyncErrors: PropTypes.string,
  timeIntervals: PropTypes.number,
  day: PropTypes.number,
  showTimeSelect: PropTypes.bool,
  time: PropTypes.bool,
  showTimeSelectOnly: PropTypes.bool,
  isClearable: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  InputProps: PropTypes.any,
  popperPlacement: PropTypes.oneOf(['bottom-start', 'bottom-end', 'top-start', 'top-end']),
};

DateTimePickerField.defaultProps = {
  popperPlacement: 'bottom-start',
  dateFormat: 'MMMM d, yyyy h:mm aa',
  utc: false,
  showTimeSelectOnly: false,
  format: '',
  timeFormat: 'h:mm aa',
  showTimeSelect: true,
  timeIntervals: 15,
  day: 0,
  disabled: false,
  time: false,
  isClearable: false,
  required: false,
  form: {
    errors: {},
    touched: {},
  },
};

export default DateTimePickerField;
